import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronDown,
  FiChevronRight,
  FiEye,
  FiTrash2,
} from "react-icons/fi";

import LoaderButton from "@/components/shared/LoaderButton";
import { DeleteConfirmModal } from "@/components/shared/DeleteConfirmModal";

export type RelatedRecord = {
  id: string;
  primary: string;
  secondary?: string;
  disabled?: boolean;
  disabledReason?: string;
};

export type RelatedGroup<T = unknown> = {
  key: string;
  label: string;
  records: Array<RelatedRecord & { data: T }>;
  renderView: (record: T, onClose: () => void) => React.ReactNode;
};

export type BlockedDeletionDialogProps = {
  show: boolean;
  onClose: () => void;
  title?: string;
  subtitle: string;
  description?: string;
  parentRecordKindLabel?: string;
  groups: RelatedGroup[];
  onDeleteRecords: (
    groupKey: string,
    recordIds: string[]
  ) => Promise<void> | void;
  onDeleteParent: () => Promise<void> | void;
  renderParentView?: (onClose: () => void) => React.ReactNode;
  deletingRecords?: boolean;
  deletingParent?: boolean;

  clearedTitle?: string;
  clearedDescription?: string;
  finalActionLabel?: string;
  finalActionIcon?: React.ReactNode;
  finalConfirmTitle?: string;
  finalConfirmMessage?: string;
  finalConfirmButtonText?: string;
};

const NAVY_ACCENT = "#1e3a5f";
const TINT_BG = "#eef4fb";
const CARD_BORDER = "#e5e7eb";
const MUTED_TEXT = "#4b5563";
const NEUTRAL_BTN_BORDER = "#d1d5db";
const NEUTRAL_BTN_TEXT = "#1f2937";

export function BlockedDeletionDialog({
  show,
  onClose,
  title = "Cannot delete this Staff record",
  subtitle,
  description,
  parentRecordKindLabel = "Staff record",
  groups,
  onDeleteRecords,
  onDeleteParent,
  renderParentView,
  deletingRecords = false,
  deletingParent = false,
  clearedTitle,
  clearedDescription,
  finalActionLabel,
  finalActionIcon,
  finalConfirmTitle,
  finalConfirmMessage,
  finalConfirmButtonText,
}: BlockedDeletionDialogProps) {
  const [selected, setSelected] = useState<Record<string, Set<string>>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(groups.map((g) => [g.key, true]))
  );
  const [viewing, setViewing] = useState<{
    groupKey: string;
    record: unknown;
  } | null>(null);
  const [viewingParent, setViewingParent] = useState(false);
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);

  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState<{
    count: number;
  } | null>(null);

  const [activeSubmissionGroupKeys, setActiveSubmissionGroupKeys] = useState<
    string[]
  >([]);

  const wasShown = useRef(false);
  useEffect(() => {
    if (show !== wasShown.current) {
      setSelected({});
      setExpanded(Object.fromEntries(groups.map((g) => [g.key, true])));
      setViewing(null);
      setViewingParent(false);
      setShowFinalConfirm(false);
      setBulkDeleteConfirm(null);
      setActiveSubmissionGroupKeys([]);
    }
    wasShown.current = show;
  }, [show, groups]);

  useEffect(() => {
    setSelected((prev) => {
      const next: Record<string, Set<string>> = {};
      const prevKeys = Object.keys(prev);
      let changed = prevKeys.length !== groups.length;
      for (const g of groups) {
        const selectableIds = new Set(
          g.records.filter((r) => !r.disabled).map((r) => r.id)
        );
        const cur = prev[g.key] ?? new Set<string>();
        const filtered = new Set(
          [...cur].filter((id) => selectableIds.has(id))
        );
        next[g.key] = filtered;
        if (filtered.size !== cur.size || !(g.key in prev)) {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    setExpanded((prev) => {
      const missing = groups.filter((g) => !(g.key in prev));
      if (missing.length === 0) return prev;
      const next = { ...prev };
      for (const g of missing) next[g.key] = true;
      return next;
    });
  }, [groups]);

  // Totals operate on *selectable* records only. Disabled rows are visible
  // but never counted toward Select all / Delete selected — otherwise the
  // counts would be misleading.
  const totalSelectable = useMemo(
    () =>
      groups.reduce(
        (sum, g) => sum + g.records.filter((r) => !r.disabled).length,
        0
      ),
    [groups]
  );
  const totalSelected = useMemo(
    () => Object.values(selected).reduce((sum, set) => sum + set.size, 0),
    [selected]
  );

  const allCleared = totalSelectable === 0 && !deletingRecords;
  const isFinalizing = totalSelectable === 0 && deletingRecords;

  const isAllSelected =
    totalSelectable > 0 && totalSelected === totalSelectable;
  const isIndeterminate = totalSelected > 0 && totalSelected < totalSelectable;

  const resolvedClearedTitle =
    clearedTitle ?? `Ready to delete this ${parentRecordKindLabel}`;
  const resolvedClearedDescription =
    clearedDescription ??
    `You can now safely delete this ${parentRecordKindLabel.toLowerCase()}.`;
  const resolvedFinalActionLabel =
    finalActionLabel ?? `Delete ${parentRecordKindLabel.toLowerCase()}`;
  const resolvedFinalActionIcon = finalActionIcon ?? <FiTrash2 aria-hidden />;
  const resolvedFinalConfirmTitle =
    finalConfirmTitle ?? `Delete ${parentRecordKindLabel}?`;
  const resolvedFinalConfirmMessage =
    finalConfirmMessage ??
    `This permanently deletes the ${parentRecordKindLabel.toLowerCase()}. This action cannot be undone.`;
  const resolvedFinalConfirmButtonText = finalConfirmButtonText ?? "Delete";

  const toggleRecord = (groupKey: string, recordId: string) => {
    setSelected((prev) => {
      const cur = new Set(prev[groupKey] ?? []);
      if (cur.has(recordId)) cur.delete(recordId);
      else cur.add(recordId);
      return { ...prev, [groupKey]: cur };
    });
  };

  const toggleGroup = (groupKey: string) => {
    const group = groups.find((g) => g.key === groupKey);
    if (!group) return;
    const selectableInGroup = group.records.filter((r) => !r.disabled);
    if (selectableInGroup.length === 0) return;
    setSelected((prev) => {
      const cur = prev[groupKey] ?? new Set<string>();
      const allInGroup =
        selectableInGroup.length > 0 && cur.size === selectableInGroup.length;
      return {
        ...prev,
        [groupKey]: allInGroup
          ? new Set()
          : new Set(selectableInGroup.map((r) => r.id)),
      };
    });
  };

  const toggleAll = () => {
    if (isAllSelected) {
      setSelected({});
      return;
    }
    const next: Record<string, Set<string>> = {};
    for (const g of groups) {
      next[g.key] = new Set(
        g.records.filter((r) => !r.disabled).map((r) => r.id)
      );
    }
    setSelected(next);
  };

  const toggleExpanded = (groupKey: string) => {
    setExpanded((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const handleDeleteSelected = () => {
    if (totalSelected === 0) return;
    setBulkDeleteConfirm({ count: totalSelected });
  };

  const handleConfirmBulkDelete = () => {
    setBulkDeleteConfirm(null);
    const submittedKeys: string[] = [];
    for (const g of groups) {
      const ids = [...(selected[g.key] ?? [])];
      if (ids.length > 0) {
        submittedKeys.push(g.key);
        onDeleteRecords(g.key, ids);
      }
    }
    setActiveSubmissionGroupKeys(submittedKeys);
    setSelected({});
  };

  const handleConfirmDeleteParent = async () => {
    setShowFinalConfirm(false);
    await onDeleteParent();
  };

  const processingMessage = (() => {
    if (activeSubmissionGroupKeys.length === 1) {
      const group = groups.find((g) => g.key === activeSubmissionGroupKeys[0]);
      if (group) {
        const friendly = group.label.replace(/\s*\([^)]*\)\s*$/, "").trim();
        if (friendly) return `Deleting ${friendly} records...`;
      }
    }
    return "Deleting records...";
  })();

  const headerNode = (
    <div style={{ minWidth: 0 }}>
      <div className="d-flex align-items-center gap-2">
        {allCleared ? (
          <FiCheckCircle size={18} style={{ flexShrink: 0 }} aria-hidden />
        ) : (
          <FiAlertTriangle size={18} style={{ flexShrink: 0 }} aria-hidden />
        )}
        <span
          style={{
            fontSize: "1.15rem",
            fontWeight: 600,
            lineHeight: 1.2,
          }}
        >
          {allCleared ? resolvedClearedTitle : title}
        </span>
      </div>
      <div
        style={{
          fontSize: "0.875rem",
          opacity: 0.85,
          marginTop: 4,
          wordBreak: "break-word",
        }}
      >
        {subtitle}
      </div>
    </div>
  );

  return (
    <>
      <Modal
        show={show && !viewing && !viewingParent}
        onHide={onClose}
        size="lg"
        centered
        scrollable
        enforceFocus={false}
        backdrop="static"
      >
        <style>{`
          .bdd-view-btn { transition: background-color 0.12s, border-color 0.12s; }
          .bdd-view-btn:hover { background-color: #f3f4f6 !important; border-color: #6b7280 !important; }
          .bdd-view-btn:focus-visible { outline: 2px solid ${NAVY_ACCENT}; outline-offset: 2px; }
          .bdd-check {
            width: 1.25rem;
            height: 1.25rem;
            cursor: pointer;
            accent-color: rgb(13, 45, 74);
            border-color: rgb(73, 80, 87);
          }
          .bdd-check:disabled {
            cursor: not-allowed;
            opacity: 0.4;
            background-color: #e5e7eb;
          }
          .bdd-row-disabled { opacity: 0.7; cursor: not-allowed; }
          .bdd-disabled-reason {
            font-size: 0.85rem;
            font-weight: 500;
            color: #374151;
            margin-top: 4px;
          }
        `}</style>
        <div className="modal-header d-block">
          <div className="d-flex align-items-start justify-content-between">
            <div className="flex-grow-1">{headerNode}</div>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onClose}
              style={{
                filter: "invert(1) grayscale(100%) brightness(200%)",
                opacity: 1,
              }}
            />
          </div>
        </div>

        <Modal.Body>
          {allCleared ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center text-center py-4"
              style={{ minHeight: "320px" }}
            >
              <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                <FiCheckCircle
                  size={22}
                  style={{ color: "#198754" }}
                  aria-hidden
                />
                <h5 className="mb-0">All related records cleared</h5>
              </div>
              <p className="text-muted mb-4 mx-auto" style={{ maxWidth: 580 }}>
                {resolvedClearedDescription}
              </p>
              {renderParentView && (
                <button
                  type="button"
                  className="btn d-inline-flex align-items-center gap-2 bdd-view-btn"
                  onClick={() => setViewingParent(true)}
                  style={{
                    border: `1.5px solid ${NAVY_ACCENT}`,
                    color: NAVY_ACCENT,
                    backgroundColor: "white",
                    fontWeight: 600,
                    padding: "8px 18px",
                    borderRadius: 8,
                    fontSize: "14px",
                  }}
                >
                  <FiEye size={16} aria-hidden />
                  View {parentRecordKindLabel.toLowerCase()}
                </button>
              )}
            </div>
          ) : isFinalizing ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center text-center py-4"
              style={{ minHeight: "320px" }}
            >
              <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
                <div
                  className="spinner-border spinner-border-sm text-primary"
                  role="status"
                  aria-hidden
                />
                <h5 className="mb-0">Processing</h5>
              </div>
              <p className="text-muted mb-0">{processingMessage}</p>
            </div>
          ) : (
            <>
              <p className="mb-3">
                {description ??
                  `The following related records must be removed before this ${parentRecordKindLabel.toLowerCase()} can be deleted. Select records to delete, or use View to inspect each one.`}
              </p>

              <div
                className="d-flex align-items-center justify-content-between mb-3"
                style={{
                  border: `1px solid ${CARD_BORDER}`,
                  borderRadius: 8,
                  background: "white",
                  padding: "12px 16px",
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="checkbox"
                    id="bdd-select-all"
                    className="form-check-input bdd-check m-0"
                    checked={isAllSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = isIndeterminate;
                    }}
                    onChange={toggleAll}
                    disabled={totalSelectable === 0}
                  />
                  <label
                    htmlFor="bdd-select-all"
                    className="m-0 fw-semibold"
                    style={{
                      cursor: totalSelectable === 0 ? "default" : "pointer",
                    }}
                  >
                    Select all ({totalSelectable})
                  </label>
                </div>
                <span style={{ color: MUTED_TEXT, fontSize: "0.875rem" }}>
                  {totalSelected} selected
                </span>
              </div>

              {groups.map((group) => {
                if (group.records.length === 0) return null;

                const groupSelected = selected[group.key] ?? new Set<string>();
                const selectableInGroup = group.records.filter(
                  (r) => !r.disabled
                );
                const isGroupAllSelected =
                  selectableInGroup.length > 0 &&
                  groupSelected.size === selectableInGroup.length;
                const isGroupIndeterminate =
                  groupSelected.size > 0 &&
                  groupSelected.size < selectableInGroup.length;
                const isExpanded = expanded[group.key] ?? true;

                return (
                  <div
                    key={group.key}
                    className="mb-3"
                    style={{
                      background: "white",
                      borderTop: `1px solid ${CARD_BORDER}`,
                      borderRight: `1px solid ${CARD_BORDER}`,
                      borderBottom: `1px solid ${CARD_BORDER}`,
                      borderRadius: "0 8px 8px 0",
                      overflow: "hidden",
                      transition: "border-color 0.15s",
                    }}
                  >
                    <div
                      className="d-flex align-items-center gap-2"
                      style={{
                        padding: "12px 16px",
                        background: isExpanded ? TINT_BG : "white",
                        borderLeft: `4px solid ${
                          isExpanded ? NAVY_ACCENT : CARD_BORDER
                        }`,
                      }}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input bdd-check m-0"
                        checked={isGroupAllSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = isGroupIndeterminate;
                        }}
                        onChange={() => toggleGroup(group.key)}
                        disabled={selectableInGroup.length === 0}
                        aria-label={`Select all ${group.label} records`}
                      />
                      <button
                        type="button"
                        className="btn btn-link p-0 d-flex align-items-center gap-2 text-decoration-none"
                        onClick={() => toggleExpanded(group.key)}
                        style={{
                          color: NAVY_ACCENT,
                          fontWeight: 700,
                          fontSize: "0.95rem",
                        }}
                        aria-expanded={isExpanded}
                      >
                        {isExpanded ? (
                          <FiChevronDown size={16} aria-hidden />
                        ) : (
                          <FiChevronRight size={16} aria-hidden />
                        )}
                        <span>{group.label}</span>
                      </button>
                      <span
                        className="ms-auto"
                        style={{
                          backgroundColor: "rgb(254, 226, 226)",
                          color: "rgb(220, 38, 38)",
                          border: "1px solid rgb(245, 128, 128)",
                          padding: "3px 10px",
                          borderRadius: 9999,
                          fontSize: "11px",
                          fontWeight: 600,
                        }}
                      >
                        {group.records.length} record
                        {group.records.length !== 1 ? "s" : ""}
                      </span>
                    </div>

                    {isExpanded && (
                      <div
                        style={{
                          padding: "10px 16px 12px 44px",
                          borderLeft: `1px solid ${CARD_BORDER}`,
                        }}
                      >
                        {group.records.map((record, idx) => (
                          <div
                            key={record.id}
                            className={`d-flex align-items-start gap-2 ${
                              record.disabled ? "bdd-row-disabled" : ""
                            }`}
                            style={{
                              borderTop:
                                idx === 0
                                  ? "none"
                                  : `1px solid rgba(15, 23, 42, 0.14)`,
                              padding: "12px 0",
                            }}
                          >
                            <input
                              type="checkbox"
                              className="form-check-input bdd-check m-0 mt-1"
                              checked={groupSelected.has(record.id)}
                              onChange={() =>
                                toggleRecord(group.key, record.id)
                              }
                              disabled={!!record.disabled}
                              aria-label={`Select ${record.primary}`}
                            />
                            <div
                              className="flex-grow-1"
                              style={{ minWidth: 0 }}
                            >
                              <div style={{ fontWeight: 500 }}>
                                {record.primary}
                              </div>
                              {record.secondary && (
                                <div
                                  style={{
                                    color: "#374151",
                                    fontSize: "0.85rem",
                                    marginTop: 2,
                                  }}
                                >
                                  {record.secondary}
                                </div>
                              )}
                              {record.disabled && record.disabledReason && (
                                <div className="bdd-disabled-reason">
                                  {record.disabledReason}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm bdd-view-btn d-flex align-items-center gap-1"
                              onClick={() =>
                                setViewing({
                                  groupKey: group.key,
                                  record: record.data,
                                })
                              }
                              style={{
                                borderRadius: 6,
                                border: "1px solid #9CA3AF",
                                backgroundColor: "white",
                                color: "#1f2937",
                                padding: "6px 12px",
                                fontWeight: 500,
                                fontSize: "13px",
                              }}
                            >
                              <FiEye size={14} aria-hidden />
                              View
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </Modal.Body>

        <Modal.Footer
          style={{
            borderTop: "1px solid #dee2e6",
            boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.06)",
          }}
        >
          <div className="d-flex w-100 align-items-center">
            <button
              type="button"
              className="btn"
              style={{
                background: "white",
                border: `1px solid ${NEUTRAL_BTN_BORDER}`,
                color: NEUTRAL_BTN_TEXT,
                borderRadius: 8,
                padding: "8px 24px",
                fontWeight: 500,
              }}
              onClick={onClose}
              disabled={deletingRecords || deletingParent}
            >
              Cancel
            </button>

            <div className="ms-auto">
              {allCleared ? (
                <LoaderButton
                  variant="danger"
                  onClick={() => setShowFinalConfirm(true)}
                  loading={deletingParent}
                  loadingText="Deleting..."
                  className="d-flex align-items-center gap-1"
                  style={{ borderRadius: 8, padding: "8px 20px" }}
                >
                  {resolvedFinalActionIcon}
                  {resolvedFinalActionLabel}
                </LoaderButton>
              ) : isFinalizing ? (
                <LoaderButton
                  variant="danger"
                  disabled
                  loading={true}
                  loadingText="Deleting..."
                  className="d-flex align-items-center gap-1"
                  style={{ borderRadius: 8, padding: "8px 20px" }}
                >
                  Deleting...
                </LoaderButton>
              ) : (
                <LoaderButton
                  variant="danger"
                  disabled={totalSelected === 0 || deletingRecords}
                  onClick={handleDeleteSelected}
                  loading={deletingRecords}
                  loadingText="Deleting..."
                  className="d-flex align-items-center gap-1"
                  style={{ borderRadius: 8, padding: "8px 20px" }}
                >
                  Delete selected ({totalSelected})
                </LoaderButton>
              )}
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {viewing &&
        (() => {
          const group = groups.find((g) => g.key === viewing.groupKey);
          if (!group) return null;
          return group.renderView(viewing.record as never, () =>
            setViewing(null)
          );
        })()}

      {viewingParent && renderParentView?.(() => setViewingParent(false))}

      {showFinalConfirm && (
        <DeleteConfirmModal
          title={resolvedFinalConfirmTitle}
          message={resolvedFinalConfirmMessage}
          confirmButtonText={resolvedFinalConfirmButtonText}
          onCancel={() => setShowFinalConfirm(false)}
          onConfirm={handleConfirmDeleteParent}
        />
      )}

      {bulkDeleteConfirm && (
        <DeleteConfirmModal
          title={`Delete ${bulkDeleteConfirm.count} record${bulkDeleteConfirm.count !== 1 ? "s" : ""}?`}
          message={`This will submit ${bulkDeleteConfirm.count} record${bulkDeleteConfirm.count !== 1 ? "s" : ""} for deletion. This action cannot be undone.`}
          confirmButtonText="Delete"
          onCancel={() => setBulkDeleteConfirm(null)}
          onConfirm={handleConfirmBulkDelete}
        />
      )}
    </>
  );
}

export default BlockedDeletionDialog;
